import React, { useMemo, useState } from 'react';
import { Search, Plus, UtensilsCrossed, Pencil, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { useFetch, Spinner } from '../lib/useFetch';
import { haccpApi, companiesApi } from '../lib/api';
import { useCrudActions } from '../lib/useCrudActions';
import EntityFormModal from '../components/EntityFormModal';
import { haccpFields } from '../lib/entitySchemas';
import { fmtDate } from '../lib/format';
import { statusBadge } from './FormazionePage';
import { useAuth } from '../context/AuthContext';

const HaccpPage = () => {
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [search, setSearch] = useState('');
  const { data: haccp, loading, reload } = useFetch(() => haccpApi.list(), []);
  const { data: companies } = useFetch(() => companiesApi.list(), []);
  const crud = useCrudActions({ api: haccpApi, label: 'documento HACCP', onChange: reload });

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || '—';

  const filtered = useMemo(() => {
    let list = haccp || [];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((h) => `${h.titolo || ''} ${h.responsabile || ''}`.toLowerCase().includes(q));
    }
    return list;
  }, [search, haccp]);

  if (loading) return <Spinner />;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#fef7d4] text-[#8a6608] flex items-center justify-center"><UtensilsCrossed size={20} /></div>
          <h2 className="text-lg font-bold text-gray-800">Gestione HACCP</h2>
        </div>
        <button onClick={() => crud.onCreate({ stato: 'valido' })} className="ro-hide px-4 py-2 rounded-full bg-[#fef7d4] text-[#8a6608] text-xs font-bold tracking-wider hover:bg-[#E6B800] flex items-center gap-2"><Plus size={14} /> NUOVO DOCUMENTO</button>
      </div>

      <div className="relative max-w-md mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca documento..." className="pl-9 h-10 border-gray-200" />
      </div>

      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-gray-500">
              <th className="text-left bg-gray-100 rounded-l-xl px-4 py-3">Documento</th>
              <th className="text-left bg-gray-100 px-4 py-3">Azienda</th>
              <th className="text-left bg-gray-100 px-4 py-3">Responsabile</th>
              <th className="text-left bg-gray-100 px-4 py-3">Rilascio</th>
              <th className="text-left bg-gray-100 px-4 py-3">Scadenza</th>
              <th className="text-left bg-gray-100 px-4 py-3">Stato</th>
              <th className="bg-gray-100 rounded-r-xl px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((h) => (
              <tr key={h.id} className="border-b border-gray-50 hover:bg-gray-50/60">
                <td className="px-4 py-3 font-semibold text-gray-800">{h.titolo}</td>
                <td className="px-4 py-3 text-gray-600 text-xs">{companyName(h.companyId)}</td>
                <td className="px-4 py-3 text-gray-700">{h.responsabile || '—'}</td>
                <td className="px-4 py-3 text-gray-700">{fmtDate(h.dataRilascio)}</td>
                <td className="px-4 py-3 text-gray-700">{fmtDate(h.scadenza)}</td>
                <td className="px-4 py-3">{statusBadge(h.stato)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => crud.onEdit(h)} title="Modifica" className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><Pencil size={13} className="text-gray-600" /></button>
                    <button onClick={() => crud.onDelete(h)} title="Elimina" className="ro-hide p-1.5 rounded-md hover:bg-gray-100"><X size={14} className="text-red-600" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-10 text-center text-sm text-gray-400">Nessun documento HACCP trovato</div>}
      </div>

      <EntityFormModal
        open={crud.open}
        onOpenChange={(o) => { if (!o) crud.closeModal(); }}
        title={crud.editing?.id ? 'Modifica documento HACCP' : 'Nuovo documento HACCP'}
        fields={haccpFields(companies || [], isClient)}
        initialValues={crud.editing || {}}
        onSubmit={crud.save}
      />
    </div>
  );
};

export default HaccpPage;
